/**
 * Types for fellowship meeting finder data
 *
 * This module defines the data structure for various recovery fellowship
 * meeting finders that users can access through the app.
 */

/**
 * Represents a recovery fellowship's meeting finder information
 */
export interface Fellowship {
  /** Unique identifier for the fellowship */
  id: string;

  /** Full name of the fellowship (e.g., "Alcoholics Anonymous") */
  name: string;

  /** Common abbreviation (e.g., "AA") */
  abbreviation: string;

  /** Brief description (1-2 sentences) */
  description: string;

  /** URL to the official meeting finder */
  meetingFinderUrl: string;

  /** Primary color hex code for branding (optional) */
  color?: string;

  /** Text color for contrast with brand color (optional) */
  textColor?: string;
}

/**
 * Fellowship categories for organization
 */
export type FellowshipCategory =
  | 'substance'      // AA, NA, CA, etc.
  | 'family'         // Al-Anon, Nar-Anon, etc.
  | 'behavioral'     // GA, SA, etc.
  | 'alternative';   // SMART Recovery, etc.

// ── Personal Meeting Management ────────────────────────────────────────────

import type { Timestamp } from 'firebase/firestore';

export type MeetingFormat = 'in-person' | 'online' | 'hybrid';
export type MeetingType = 'open' | 'closed';
export type MeetingFormatType = 'speaker' | 'discussion' | 'book_study' | 'step_study' | 'beginner';

export const WEEKLY_ROLES = [
  'Foundation (in-person)',
  'Foundation (online)',
  'Greeter (in-person)',
  'Greeter (online)',
  'Facilitator',
  'Coffee/Refreshments',
  'Literature',
  'Secretary',
  'Chips/Tokens',
  'Tech (online host)',
  'Treasurer',
  'Other',
] as const;

export const LONG_TERM_POSITIONS = [
  'Secretary',
  'Treasurer',
  'GSR',
  'Intergroup Representative',
  'H&I',
  'PI',
  'Literature Chair',
  'Activities Chair',
  'District Committee Member (DCM)',
  'Other',
] as const;

export interface SavedMeeting {
  id: string;
  name: string;
  fellowship: string;
  fellowshipName: string;
  isHomeGroup: boolean;
  format: MeetingFormat;
  dayOfWeek: string[];
  time: string;
  address?: string;
  city?: string;
  zoomLink?: string;
  zoomMeetingId?: string;
  zoomPasscode?: string;
  meetingType?: MeetingType;
  meetingFormat?: MeetingFormatType;
  notes?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface MeetingAttendance {
  id: string;
  meetingId: string;
  meetingName: string;
  attendedDate: string;
  weeklyRole?: string;
  notes?: string;
  createdAt: Timestamp;
}

export interface ServicePosition {
  id: string;
  meetingId?: string;
  meetingName?: string;
  positionName: string;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  notes?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}
